/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { TFunction } from "i18next";
import SimpleReactValidator from "simple-react-validator";
import disposableEmail from "disposable-email";
import { REGEX_MOBILE_NUMBER, REGEX_PASSWORD } from "../constant";
import parsePhoneNumberFromString, {
  parsePhoneNumber,
} from "libphonenumber-js";

interface IUpdatePasswordForm {
  password: string;
  confirmPassword: string;
}
const usrNameRegex = /^[a-zA-Z\\.\\-\\_0-9]{1,}$/;

export const URL_PATTERN =
  /^(https?:\/\/)([\w.-]+)(:[0-9]+)?(\/[^?#]*)?(\?[^#]*)?(#.*)?$/;

/** Validates an absolute http(s) URL; pass allowHttp = false to require https. */
export const isValidURL = (val: string, allowHttp = true): boolean => {
  if (!URL_PATTERN.test(val)) return false;
  try {
    return allowHttp || new URL(val).protocol === "https:";
  } catch {
    return false;
  }
};

export const emailValidator = (t: TFunction) => {
  return {
    disposableEmail: {
      message: `${t("restricted_email_domain")}`,
      rule: (email: string) => {
        return disposableEmail.validate(email);
      },
      // @ts-ignore
      required: true,
    },
  };
};

export const SignUpFormValidator = (t: TFunction): SimpleReactValidator =>
  new SimpleReactValidator({
    messages: {
      required: t("form:validation.required"),
    },
    className: "form-text text-danger",
    validators: {
      passwordPattern: {
        message: `${t("error_messages.password_invalid")}`,
        rule: (password: string) => {
          return REGEX_PASSWORD.test(password);
        },
        // @ts-ignore
        required: true,
      },
      comparePassword: {
        message: `${t("error_messages.password_same_value")}`,
        rule: ({ password, confirmPassword }: IUpdatePasswordForm) => {
          return password === confirmPassword;
        },
        // @ts-ignore
        required: true,
      },
      userNamePattern: {
        message: `${t("error_messages.username_pattern")}`,
        rule: (username: string) => {
          if (username && username !== "") {
            return usrNameRegex.test(username);
          }
          return true;
        },
      },
      userNameMinMax: {
        message: `${t("error_messages.username_length")}`,
        rule: (username: string) => {
          if (username && username !== "") {
            return username.length >= 1 && username.length <= 100;
          }
          return true;
        },
      },
      validMobileNumber: {
        message: `${t("common:mobile_number_invalid")}`,
        rule: ({
          mobilePhone,
          nationalNumber,
        }: {
          mobilePhone: string;
          nationalNumber?: string;
        }) => {
          if (nationalNumber?.length > 0) {
            if (!REGEX_MOBILE_NUMBER.test(mobilePhone)) return false;
            const prefixValue = parsePhoneNumberFromString(mobilePhone);
            if (
              prefixValue &&
              prefixValue?.country &&
              prefixValue?.nationalNumber
            ) {
              return parsePhoneNumber(
                prefixValue.nationalNumber,
                prefixValue.country
              ).isValid();
            }
            return false;
          }
        },
        // @ts-ignore
        required: true,
      },
      ...emailValidator(t),
    },
  });
